import { useMemo, useState } from 'react'
import CreerAmeliorerModalContainer from '../../components/creer-ameliorer-modal-container'
import type { CreationKind } from '../../components/creer-ameliorer-modal'
import { classesSubtitle } from './teacher-classes-subtitle'
import { useTeacherDashboard } from '../../hooks/use-teacher-dashboard'
import {
  IdentificationCard,
  BookBookmark,
  ClockCountdown,
  Sparkle,
  PushPin,
  CalendarBlank,
  Users,
  Chalkboard,
  CaretRight,
  File,
  PencilSimple,
  ClipboardText,
  PaperPlaneTilt,
  Quotes,
} from '@phosphor-icons/react'

import { cn } from '@/lib/utils'
import { Link } from '../../lib/routing'
import PdfPanel from '../../components/pdf-panel'
import { ScheduleDropZone } from '../../components/schedule-drop-zone'
import { Separator } from '@/components/ui/separator'
import { IconPad, type IconPadVariant, type IconPadSize } from '@/components/ui/icon-pad'
import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header'
import { useLocalStorage } from '../../hooks/use-local-storage'
import styles from './teacher-dashboard.module.css'
import { QuickAccessList } from '../../components/quick-access-list'
import { buildTeacherQuickAccess, TEACHER_QUICK_ACCESS_DEFAULT_PINS } from '../../data/teacher-quick-access-catalog'

/* ── Modèle de données ──────────────────────────────────────────────────────── */

export type PadType = 'b' | 'g' | 'n' | 'p' | 'o'

/* En cours = 3 derniers documents en brouillon (NS-en-cours-drafts).           */
export interface RowItem {
  id: string
  title: string
  sub: string
}

export interface ClassLevel {
  grade: string
  ordinal: string
}

export interface ClassItem {
  id: string
  name: string
  level: ClassLevel | null
  studentCount: number
  subjects: string[]
}

export interface GreetingData {
  timeMessage: string
  quoteText: string
  quoteAuthor: string
  quoteAuthorRole: string
  aiNote: string | null
}

export interface ScheduleFile {
  name: string
  fileSize: number
  signedUrl: string | null
  uploadedAt: string
}

export interface TeacherDashboardData {
  teacher: { firstName: string }
  greeting: GreetingData | null
  rows: RowItem[]
  classes: ClassItem[]
  scheduleFile: ScheduleFile | null
}


/* ── Sections et état par défaut ─────────────────────────────────────────────── */

const SECTION_DEFAULTS: Record<string, boolean> = {
  'acces-rapide':    true,
  'en-cours':        true,
  'mes-classes':     true,
  'creer':           true,
  'emploi-du-temps': false,
}

/* ── Pad helper ─────────────────────────────────────────────────────────────── */

const PAD_VARIANT: Record<PadType, IconPadVariant> = {
  b: 'blue', g: 'green', n: 'neutral', p: 'purple', o: 'orange',
}

function Pad({ type, size = 'lg', className, children }: {
  type: PadType; size?: IconPadSize; className?: string; children: React.ReactNode
}) {
  return <IconPad variant={PAD_VARIANT[type]} size={size} className={className}>{children}</IconPad>
}

/* ── En-tête de section ─────────────────────────────────────────────────────── */

function SectionSummary({ type, icon, title, sub, className }: {
  type: PadType; icon: React.ReactNode; title: string; sub: React.ReactNode; className?: string
}) {
  return (
    <summary className={cn('flex items-center gap-3 p-[14px_18px] cursor-pointer select-none list-none', className)}>
      <Pad type={type} size="md">{icon}</Pad>
      <span className="flex-1 min-w-0">
        <b className="block text-[17px] font-bold tracking-[-0.015em] leading-[1.35]">{title}</b>
        <span className="block text-[13px] text-muted-foreground font-medium">{sub}</span>
      </span>
      <CaretRight size={18} className={cn('car text-naia-muted-2 flex-shrink-0 transition-transform duration-[0.18s]', styles.car)} />
    </summary>
  )
}

/* ── Tile (bouton ou lien) ──────────────────────────────────────────────────── */

function Tile({ type, icon, title, sub, href, onClick }: {
  type: PadType; icon: React.ReactNode; title: string; sub: string; href?: string; onClick?: () => void
}) {
  const inner = (
    <>
      <Pad type={type} size="md" className={styles.tilePad}>{icon}</Pad>
      <span className="flex flex-col min-w-0 leading-[1.4]">
        <span className="text-[15px] font-bold tracking-[-0.005em]">{title}</span>
        <span className="text-[12.5px] text-muted-foreground font-medium">{sub}</span>
      </span>
    </>
  )
  const cls = cn(
    'mtile flex items-start gap-3 p-[14px] rounded-md bg-muted text-left',
    'transition-[transform,box-shadow,background] duration-[0.18s]',
    'w-full border-none cursor-pointer text-foreground font-sans no-underline',
    'hover:bg-naia-sky hover:-translate-y-0.5 hover:shadow-card',
  )
  return href
    ? <a className={cls} href={href}>{inner}</a>
    : <button type="button" className={cls} onClick={onClick}>{inner}</button>
}

/* ── Composant principal ────────────────────────────────────────────────────── */

export interface TeacherStats {
  classroomsCount: number
  activeAssignmentsCount: number
  threadsCount: number
}

export function TeacherDashboardView({
  data,
  stats,
  onUpload,
}: {
  /* Volontairement REQUIS : une valeur par defaut ferait afficher le jeu de
     demonstration — un prenom et des classes qui ne sont pas ceux du lecteur —
     des que l'appelant n'a rien a passer. */
  data: TeacherDashboardData
  stats?: TeacherStats | null
  onUpload?: (file: File) => Promise<void>
}) {
  const { teacher, greeting, rows, classes, scheduleFile } = data

  const c = stats?.classroomsCount ?? 0
  const a = stats?.activeAssignmentsCount ?? 0
  const t = stats?.threadsCount ?? 0

  const quickEntries = useMemo(() => buildTeacherQuickAccess(classes), [classes])

  const [expanded, setExpanded] = useLocalStorage<Record<string, boolean>>(
    'naia.teacher-dashboard.sections',
    SECTION_DEFAULTS,
  )

  /* Lire e.currentTarget.open (état réel après toggle) au lieu de !prev[id].  */
  /* Sinon le reconciliation React initial (qui pose open=true sur un <details>  */
  /* fraîchement monté) fire onToggle et crée une boucle infinie.               */
  const handleToggle = (id: string, isOpen: boolean) =>
    setExpanded(prev =>
      prev[id] === isOpen ? prev : { ...prev, [id]: isOpen },
    )

  const open = (id: string) => expanded[id] ?? SECTION_DEFAULTS[id] ?? false

  const [schedulePdfOpen, setSchedulePdfOpen] = useState(false)
  const [creationKind, setCreationKind] = useState<CreationKind | null>(null)

  const greetingLine = greeting ? (greeting.aiNote ?? greeting.timeMessage) : null

  return (
    <div className={cn('min-h-screen bg-background text-foreground antialiased', styles.root)}>
      <HeaderContent width="lg" showBurger>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 w-full min-w-0">
          <div className="min-w-0 sm:flex-1">
            <h1 className="m-0 min-w-0 truncate font-serif font-medium leading-tight text-foreground text-[16px] sm:text-[18px]">
              Bonjour {teacher.firstName}
            </h1>
            {greetingLine && (
              <p className="m-0 mt-0.5 font-serif text-[11.5px] leading-snug text-muted-foreground truncate">
                {greetingLine}
              </p>
            )}
            {stats && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-[11px] font-semibold text-foreground">
                  <IdentificationCard size={12} weight="fill" className="text-naia-blue" />
                  {c} classe{c !== 1 ? 's' : ''}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-[11px] font-semibold text-foreground">
                  <ClipboardText size={12} weight="fill" className="text-naia-blue" />
                  {a} devoir{a !== 1 ? 's' : ''} envoyé{a !== 1 ? 's' : ''}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-[11px] font-semibold text-foreground">
                  <BookBookmark size={12} weight="fill" className="text-naia-blue" />
                  {t} conversation{t !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          {greeting?.quoteText && (
            <figure className="m-0 min-w-0 shrink-0 sm:w-[300px] sm:border-l sm:border-border sm:pl-5">
              <figcaption className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-naia-blue">
                <Quotes size={11} weight="fill" />
                Citation du jour
              </figcaption>
              <blockquote className="m-0 mt-1 font-serif text-[12.5px] sm:text-[13px] leading-[1.3] tracking-[-0.01em] text-foreground line-clamp-2">
                « {greeting.quoteText} »
              </blockquote>
              <p className="m-0 mt-1 text-[10.5px] leading-[1.3] text-muted-foreground truncate">
                <span className="font-semibold text-foreground">{greeting.quoteAuthor}</span>
                {greeting.quoteAuthorRole && <> · {greeting.quoteAuthorRole}</>}
              </p>
            </figure>
          )}
        </div>
      </HeaderContent>
      <div className="max-w-[1120px] mx-auto px-6 pt-[22px] pb-[72px] flex flex-col gap-[14px] max-sm:px-[14px] max-sm:pt-[14px] max-sm:pb-14 max-sm:gap-[10px]">

        {/* ── 1. Accès rapide ── */}
        <details open={open('acces-rapide')} onToggle={(e) => handleToggle('acces-rapide', (e.currentTarget as HTMLDetailsElement).open)}
          className="bg-card border border-border rounded-card shadow-card overflow-hidden">
          <SectionSummary type="b" icon={<PushPin size={22} weight="fill" />}
            title="Accès rapide" sub="vos raccourcis, épinglés par vous" />
          <div className="px-5 pb-5">
            <QuickAccessList
              entries={quickEntries}
              defaultPinned={TEACHER_QUICK_ACCESS_DEFAULT_PINS}
            />
          </div>
        </details>

        {/* ── 2. En cours ── */}
        <details open={open('en-cours')} onToggle={(e) => handleToggle('en-cours', (e.currentTarget as HTMLDetailsElement).open)}
          className="bg-card border border-border rounded-card shadow-card overflow-hidden">
          <SectionSummary type="b" icon={<ClockCountdown size={22} weight="fill" />}
            title="En cours" sub="derniers documents en brouillon" />
          <div className="px-5 pb-5 flex flex-col gap-[10px]">
            {rows.length > 0
              ? rows.map(row => (
                  <Link key={row.id} to={`/teacher/library/${row.id}`} className="flex items-center gap-[14px] bg-muted rounded-md p-[14px_16px] transition-[background] duration-150 w-full text-left border-none cursor-pointer text-foreground font-sans no-underline hover:bg-naia-sky">
                    <div className="flex-1 min-w-0">
                      <span className="block text-[15.5px] font-bold tracking-[-0.005em] whitespace-nowrap overflow-hidden text-ellipsis">{row.title}</span>
                      <span className="block text-[13px] text-muted-foreground font-medium mt-0.5">{row.sub}</span>
                    </div>
                    <CaretRight size={18} className="text-naia-muted-2 flex-shrink-0" />
                  </Link>
                ))
              : (
                  <div className="flex flex-col items-start gap-3 py-1">
                    <span className="text-[14px] text-muted-foreground">
                      Aucun document en brouillon. Vos cours et évaluations en cours de rédaction apparaîtront ici.
                    </span>
                    <button
                      type="button"
                      onClick={() => setCreationKind('course')}
                      className="inline-flex items-center gap-2 min-h-11 px-4 rounded-full bg-naia-blue text-white text-[13.5px] font-bold border-none cursor-pointer font-sans hover:bg-naia-blue-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-naia-blue-deep"
                    >
                      <Sparkle size={16} weight="fill" />
                      Commencer un cours
                    </button>
                  </div>
                )
            }
          </div>
        </details>

        {/* ── 3. Mes classes ── */}
        <details open={open('mes-classes')} onToggle={(e) => handleToggle('mes-classes', (e.currentTarget as HTMLDetailsElement).open)}
          className="bg-card border border-border rounded-card shadow-card overflow-hidden">
          <SectionSummary type="g" icon={<IdentificationCard size={22} weight="fill" />}
            title="Mes classes" sub={classesSubtitle(classes)} />
          <div className="px-5 pb-5 flex flex-col gap-[10px]">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3">
              {classes.map(cls => (
                <Link key={cls.id} to={`/teacher/classes/${cls.id}`} className="bg-muted rounded-md p-[16px] flex flex-col gap-2 transition-[transform,box-shadow] duration-200 text-left border-none cursor-pointer text-foreground font-sans no-underline hover:-translate-y-[3px] hover:shadow-card">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 flex-shrink-0 rounded-card flex items-center justify-center text-[16px] font-extrabold tracking-[-0.02em] bg-naia-sky text-naia-blue-deep">
                      {cls.level
                        ? <span>{cls.level.grade}<sup>{cls.level.ordinal}</sup></span>
                        : <Chalkboard size={20} weight="fill" />}
                    </span>
                    <h3 className="m-0 min-w-0 flex-1 text-[17px] font-extrabold tracking-[-0.015em] leading-snug line-clamp-2">{cls.name}</h3>
                  </div>
                  {cls.studentCount > 0 && (
                    <span className="flex items-center justify-end gap-1.5 text-[12.5px] text-muted-foreground font-medium">
                      <Users size={15} weight="fill" /> <b className="font-mono font-semibold text-foreground tabular-nums">{cls.studentCount}</b> élèves
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </details>

        {/* ── 4. Créer ou améliorer ── */}
        <details open={open('creer')} onToggle={(e) => handleToggle('creer', (e.currentTarget as HTMLDetailsElement).open)}
          className="bg-card border border-border rounded-card shadow-card overflow-hidden">
          <SectionSummary type="p" icon={<Sparkle size={22} weight="fill" />}
            title="Créer ou améliorer" sub="cours, exercice, évaluation" />
          <div className="flex flex-col gap-[18px] px-5 pb-5 pt-0.5">
            <div className="flex flex-col gap-[10px]">
              <span className="flex items-center gap-[11px] text-[12px] font-extrabold tracking-[0.11em] uppercase text-muted-foreground">
                Qu'est-ce que vous préparez ?<Separator className="flex-1" />
              </span>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(196px,1fr))] gap-[10px]">
                <Tile type="b" icon={<File size={20} weight="fill" />}          title="Un cours"             sub="Une séance à part entière"          onClick={() => setCreationKind('course')} />
                <Tile type="g" icon={<PencilSimple size={20} weight="fill" />}  title="Un exercice"          sub="Entraînement, quiz, trois niveaux"  onClick={() => setCreationKind('exercice_sheet')} />
                <Tile type="p" icon={<ClipboardText size={20} weight="fill" />} title="Une évaluation notée" sub="Note et appréciation"               onClick={() => setCreationKind('exam')} />
              </div>
              <p className="m-0 mt-0.5 text-[13px] leading-snug text-muted-foreground">
                Un devoir maison se prépare comme un exercice, puis s'envoie avec une date de remise
                depuis votre bibliothèque.
              </p>
              <Link
                to="/teacher/library"
                className="inline-flex w-fit items-center gap-2 min-h-11 px-3 -ml-3 rounded-md font-sans text-[13.5px] font-bold text-naia-blue no-underline hover:bg-naia-info-bg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-naia-blue"
              >
                <PaperPlaneTilt size={16} weight="fill" />
                Envoyer en devoir depuis ma bibliothèque
              </Link>
            </div>
          </div>
        </details>

        {/* ── 5. Mon emploi du temps ── */}
        <details open={open('emploi-du-temps')} onToggle={(e) => handleToggle('emploi-du-temps', (e.currentTarget as HTMLDetailsElement).open)}
          className="bg-card border border-border rounded-card shadow-card overflow-hidden">
          <SectionSummary type="g" icon={<CalendarBlank size={22} weight="fill" />}
            title="Mon emploi du temps"
            sub={scheduleFile
              ? `Mis à jour le ${new Date(scheduleFile.uploadedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`
              : 'Aucun fichier déposé'} />
          <div className="px-5 pb-5 flex flex-col gap-[10px]">
            <ScheduleDropZone
              scheduleFile={scheduleFile ?? null}
              onUpload={onUpload ?? ((_: File) => Promise.resolve())}
              onOpen={() => setSchedulePdfOpen(true)}
            />
          </div>
        </details>
        {schedulePdfOpen && scheduleFile?.signedUrl && (
          <PdfPanel
            url={scheduleFile.signedUrl}
            title="Mon emploi du temps"
            onClose={() => setSchedulePdfOpen(false)}
          />
        )}

        {creationKind && (
          <CreerAmeliorerModalContainer
            defaultKind={creationKind}
            onClose={() => setCreationKind(null)}
          />
        )}

      </div>
    </div>
  )
}

/* ── Page câblée au hook ────────────────────────────────────────────────────── */

function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <HeaderContent width="lg" showBurger>
        <div className="animate-pulse rounded-md bg-muted h-5 w-40" />
      </HeaderContent>
      <div className="max-w-[1120px] mx-auto px-6 pt-[22px] pb-[72px] flex flex-col gap-[14px]">
        <div className="px-2 pt-1 pb-2">
          <div className="animate-pulse rounded-md bg-muted h-8 w-48 mb-3" />
          <div className="animate-pulse rounded-md bg-muted h-4 w-64 mb-2" />
          <div className="animate-pulse rounded-md bg-muted h-4 w-56" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="mt-4">
            <div className="animate-pulse rounded-md bg-muted h-5 w-32 mb-2" />
            <div className="animate-pulse rounded-md bg-muted h-16 w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

function DashboardError({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-background">
      <HeaderContent width="lg" showBurger>
        <h1 className={HEADER_TITLE_CLASS}>Tableau de bord</h1>
      </HeaderContent>
      <div className="max-w-[1120px] mx-auto px-6 pt-[22px] pb-[72px]">
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <p className="text-[15px] font-semibold text-foreground">Impossible de charger le tableau de bord</p>
          <p className="text-[13px] text-muted-foreground max-w-xs">{message}</p>
        </div>
      </div>
    </div>
  )
}

export default function TeacherDashboard() {
  const { data, stats, loading, error, uploadSchedule } = useTeacherDashboard()

  if (loading) return <DashboardSkeleton />
  if (error) return <DashboardError message={error.message} />
  if (!data) return <DashboardError message="Vos classes et vos documents n'ont pas pu être récupérés. Réessayez dans un instant." />

  return <TeacherDashboardView data={data} stats={stats} onUpload={uploadSchedule} />
}
